import type { CSSProperties } from 'react';
import type { Language } from '../contexts/TranslationContext';
import type { DeferredVideoSources } from '../utils/deferredVideo';

export type PresentationDemoKey = 'consultant' | 'transport';

export interface PresentationDemo {
  key: PresentationDemoKey;
  labelKey: string;
  thumbnailSrc: string;
  videoLabelKey: string;
  videoSources: Record<Language, DeferredVideoSources>;
  poster: string;
}

export type PresentationVideoViewport = 'mobile' | 'desktop';

export interface PresentationVideoViewportLayout {
  aspectRatio: string;
  objectFit: 'contain' | 'cover';
  objectPosition: string;
}

export interface PresentationVideoLayout {
  desktop: PresentationVideoViewportLayout;
  height: number;
  mobile: PresentationVideoViewportLayout;
  width: number;
}

export type PresentationVideoLayoutStyle = CSSProperties & {
  '--presentation-video-desktop-aspect-ratio': string;
  '--presentation-video-desktop-object-fit': string;
  '--presentation-video-desktop-object-position': string;
  '--presentation-video-mobile-aspect-ratio': string;
  '--presentation-video-mobile-object-fit': string;
  '--presentation-video-mobile-object-position': string;
};

const EN_CONSULTANT_VIDEO_LAYOUT: PresentationVideoLayout = {
  desktop: {
    aspectRatio: '10 / 9',
    objectFit: 'cover',
    objectPosition: 'top',
  },
  height: 1080,
  mobile: {
    aspectRatio: '10 / 9',
    objectFit: 'cover',
    objectPosition: 'top',
  },
  width: 1080,
};

const EN_HERO_VIDEO_LAYOUT: PresentationVideoLayout = {
  ...EN_CONSULTANT_VIDEO_LAYOUT,
  desktop: {
    ...EN_CONSULTANT_VIDEO_LAYOUT.desktop,
    aspectRatio: '4 / 3',
  },
  mobile: {
    ...EN_CONSULTANT_VIDEO_LAYOUT.mobile,
    aspectRatio: '4 / 3',
  },
};

const FR_CONSULTANT_VIDEO_LAYOUT: PresentationVideoLayout = {
  desktop: {
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  height: 1080,
  mobile: {
    aspectRatio: '7 / 6',
    objectFit: 'cover',
    objectPosition: 'center 64%',
  },
  width: 1080,
};

const EN_TRANSPORT_AUDITOR_VIDEO_LAYOUT: PresentationVideoLayout = {
  desktop: {
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    objectPosition: 'left top',
  },
  height: 1080,
  mobile: {
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    objectPosition: 'left top',
  },
  width: 1920,
};

const FR_TRANSPORT_AUDITOR_VIDEO_LAYOUT: PresentationVideoLayout = {
  desktop: {
    aspectRatio: '10 / 9',
    objectFit: 'cover',
    objectPosition: 'center top',
  },
  height: 1080,
  mobile: {
    aspectRatio: '10 / 9',
    objectFit: 'cover',
    objectPosition: 'center top',
  },
  width: 1080,
};

export const PRESENTATION_VIDEO_LAYOUTS: Record<
  Language,
  Record<PresentationDemoKey, PresentationVideoLayout>
> = {
  en: {
    consultant: EN_CONSULTANT_VIDEO_LAYOUT,
    transport: EN_TRANSPORT_AUDITOR_VIDEO_LAYOUT,
  },
  fr: {
    consultant: FR_CONSULTANT_VIDEO_LAYOUT,
    transport: FR_TRANSPORT_AUDITOR_VIDEO_LAYOUT,
  },
};

export function getPresentationVideoLayout(
  demoKey: PresentationDemoKey,
  language: Language,
): PresentationVideoLayout {
  return PRESENTATION_VIDEO_LAYOUTS[language][demoKey];
}

export function getPresentationHeroVideoLayout(language: Language): PresentationVideoLayout {
  return language === 'fr'
    ? FR_CONSULTANT_VIDEO_LAYOUT
    : EN_HERO_VIDEO_LAYOUT;
}

export function getPresentationVideoLayoutStyle(
  layout: PresentationVideoLayout,
): PresentationVideoLayoutStyle {
  return {
    '--presentation-video-desktop-aspect-ratio': layout.desktop.aspectRatio,
    '--presentation-video-desktop-object-fit': layout.desktop.objectFit,
    '--presentation-video-desktop-object-position': layout.desktop.objectPosition,
    '--presentation-video-mobile-aspect-ratio': layout.mobile.aspectRatio,
    '--presentation-video-mobile-object-fit': layout.mobile.objectFit,
    '--presentation-video-mobile-object-position': layout.mobile.objectPosition,
  };
}

const CONSULTANT_VIDEO_SOURCES: Record<Language, DeferredVideoSources> = {
  en: {
    mp4: '/assets/agents/videos/en/agent-adr-1x1.mp4',
    webm: '/assets/agents/videos/en/agent-adr-1x1.webm',
  },
  fr: {
    mp4: '/assets/agents/videos/fr/agent-adr-1x1.mp4',
    webm: '/assets/agents/videos/fr/agent-adr-1x1.webm',
  },
};

const TRANSPORT_AUDITOR_VIDEO_SOURCES: Record<Language, DeferredVideoSources> = {
  en: {
    mp4: '/assets/agents/videos/en/agent-auditor-1x1.mp4',
    webm: '/assets/agents/videos/en/agent-auditor-1x1.webm',
  },
  fr: {
    mp4: '/assets/agents/videos/fr/agent-auditor-1x1.mp4',
    webm: '/assets/agents/videos/fr/agent-auditor-1x1.webm',
  },
};

export const PRESENTATION_DEMOS: readonly PresentationDemo[] = [
  {
    key: 'consultant',
    labelKey: 'presentation.demoTabs.consultant',
    thumbnailSrc: '/assets/agents/agent_adviser_mini.webp',
    videoLabelKey: 'presentation.consultantDemoLabel',
    videoSources: CONSULTANT_VIDEO_SOURCES,
    poster: '/assets/agents/consultant_FR-video-poster-hd.png',
  },
  {
    key: 'transport',
    labelKey: 'presentation.demoTabs.transport',
    thumbnailSrc: '/assets/agents/agent_analyzer_mini.webp',
    videoLabelKey: 'presentation.transportDemoLabel',
    videoSources: TRANSPORT_AUDITOR_VIDEO_SOURCES,
    poster: '/assets/agents/consultant_FR-video-poster-hd.png',
  },
];
